import { CloudUploadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Space } from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFetch } from 'hooks/useFetch';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import UploadHistoryDrawer from 'pages/UploadHistory';
import { useEffect, useMemo, useState } from 'react';
import { BankAccountsService } from 'services/BankAccountsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { BankAccountsForm } from './BankAccountsForm';
import { BankAccountsTable } from './BankAccountsTable';

const PAGE_TITLE = 'Банковские счета';

function BankAccounts() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [banks] = useFetch('getallbank/', []);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [openUploadHistory, setOpenUploadHistory] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    bin_iin: null,
    iik: null,
    bank_name: null,
    client_id: '',
    currency: null,
  });

  const recordFields = useMemo(() => {
    return [
      { label: 'Наименование', key: 'name_ru' },
      { label: 'Номер счёта', key: 'account' },
      { label: 'Валюта', key: 'currency' },
      { label: 'ИИН/БИН', key: 'bin_iin' },
      { label: 'Клиент', key: 'client_name' },
      { label: 'БИК', key: 'bik' },
      { label: 'Код', key: 'code' },
      {
        label: 'Статус',
        key: 'active',
        computedValue: activeRecord?.active ? 'Активный' : 'Не активный',
      },
    ];
  }, [activeRecord]);

  useEffect(() => {
    fetchBankAccounts();
  }, [page, perPage, filters]);

  const fetchBankAccounts = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await BankAccountsService.getBankAccounts(
        page,
        perPage,
        filters
      );

      setBankAccounts(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await BankAccountsService.deleteBankAccount(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchBankAccounts();
        message.success('Статус банк. счёта изменен на НЕ АКТИВЕН');
      }
    } catch (err) {
      message.error('Не удалось изменить статус банк. счёта');
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const handleClientDataChange = (clientData) => {
    setSelectedClientData(clientData);
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
          onSelectedClientDataChange={handleClientDataChange}
        />
        <Space>
          <AccessControl pageId="history" action="create">
            <Button
              onClick={() => setOpenUploadHistory(true)}
              icon={<CloudUploadOutlined />}
            >
              Загрузки
            </Button>
          </AccessControl>
          <AccessControl pageId="bankAccounts" action="create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
            >
              Создать
            </Button>
          </AccessControl>
        </Space>
      </ActionsWrapper>

      <BankAccountsTable
        items={bankAccounts}
        banks={banks}
        loading={loading}
        totalRows={totalRows}
        current={page}
        pageSize={perPage}
        filters={filters}
        onChange={setPagination}
        onRowClick={openDrawer}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <RecordInfoDrawer
        record={activeRecord}
        recordFields={recordFields}
        drawerTitle={activeRecord?.name_ru}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите поменять статус этого банк. счёта?"
        pageId="bankAccounts"
      />

      <BankAccountsForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchBankAccounts}
        onClose={closeForm}
        defaultIin={selectedClientData?.bin_iin}
      />

      <UploadHistoryDrawer
        open={openUploadHistory}
        onClose={() => setOpenUploadHistory(false)}
        uploadUrl="/org/v1/bank/accounting/import/"
      />
    </>
  );
}

export { BankAccounts };
